import * as Sentry from '@sentry/react';
import envConfig from 'configs/envConfig';
import monitoringService from 'infrastructure/serviceHandlers/Monitoring/Monitoring';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import {
  categorizeError,
  getCategoryDescription,
  isExpectedApiError
} from 'utils/sentryHelpers';

const SENTRY_ENABLED_ENVS = [
  'prod',
  'staging',
  'luna-stg',
  'luna',
  'homiq-stg',
  'homiq-prod',
  'haloview-stg',
  'haloview-prod',
  'waiscen-stg',
  'waiscen-prod',
  'newaieye-stg',
  'newaieye-prod',
  'iv_sandbox-prod'
];

const turnONSentryEvents = SENTRY_ENABLED_ENVS.includes(
  String(envConfig('env'))
);

// prod, luna and the partner *-prod envs carry nearly all traffic; staging and
// *-stg together send <200 replays/month, so only prod-like envs need the lower
// healthy-session replay rate.
const isProdLikeEnv = !/stg|staging/.test(String(envConfig('env')));

// Expected API outcomes (token-expiry 401s, app-bridge 404 probes) are kept at
// 2% so the issue stays alive for rate alerts without burning the quota.
const EXPECTED_API_SAMPLE_RATE = 0.02;

// Third-party telemetry/analytics endpoints we don't control. They
// intermittently return 4xx/5xx, which httpClientIntegration would otherwise
// report as bogus "HTTP Client Error" exceptions. These are noise, not app errors.
const THIRD_PARTY_NOISE_HOSTS = [
  'clarity.ms',
  'google-analytics.com',
  'analytics.google.com',
  'doubleclick.net',
  // Firebase infra endpoints (config/telemetry) we don't control. Non-critical
  // 4xx/5xx here (e.g. Remote Config 503) fall back to cached/default values.
  'firebaseremoteconfig.googleapis.com',
  'firebaseinstallations.googleapis.com'
];

if (turnONSentryEvents) {
  monitoringService.initialize({
    dsn: 'https://67b4cbdeb9d58f83fb9855877e73191f@o4507616560414720.ingest.us.sentry.io/4507616658063360',
    integrations: [
      // 5xx only: 4xx responses are expected outcomes (401 token expiry, 404
      // probes) and were ~19k error events + ~6k error-replays a month. They
      // remain visible as breadcrumbs and via the reducers' captureException.
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[500, 599]]
      }),

      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      // browserProfilingIntegration removed: it is a no-op without
      // profilesSampleRate set, and it costs bundle bytes + a
      // Document-Policy requirement.
      // browserTracingIntegration removed: it registers under the same
      // "BrowserTracing" name as reactRouterV6BrowserTracingIntegration above
      // and was overriding its router-aware instrumentation.

      // replayIntegration is added lazily after init (see ./sentryReplay) so
      // its ~73KB ships as an async chunk off the critical path.
      Sentry.extraErrorDataIntegration({ depth: 6 })
    ],

    release: envConfig('releaseVersion'),
    environment: envConfig('env'),
    normalizeDepth: 6,
    // Performance Monitoring. The webview_boot transaction (utils/bootTrace)
    // is the FCM-2662 baseline — sample it at 100% so p50/p95 reflect every
    // open; everything else keeps the previous 10%.
    tracesSampler: (samplingContext: { name: string }) =>
      samplingContext.name === 'webview_boot' ? 1.0 : 0.1,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // Session Replay
    // Healthy-session recording: 1% in prod-like envs (was 10%, ~20k replays a
    // month), unchanged 10% on staging where volume is negligible and QA wants
    // the recordings. Error-triggered replays are governed by
    // replaysOnErrorSampleRate + beforeErrorSampling (see ./sentryReplay).
    replaysSessionSampleRate: isProdLikeEnv ? 0.01 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    // This option is required for capturing headers and cookies.
    sendDefaultPii: true,
    beforeSend(event: Sentry.Event, hint: Sentry.EventHint) {
      // Drop HTTP-client errors originating from third-party telemetry/analytics
      // endpoints (e.g. Microsoft Clarity's /collect). We don't control these and
      // their intermittent 4xx/5xx responses are noise, not application errors.
      const failedRequestUrl = event.request?.url || '';
      if (
        failedRequestUrl &&
        THIRD_PARTY_NOISE_HOSTS.some((host) => failedRequestUrl.includes(host))
      ) {
        return null;
      }

      if (!event.exception && !event.message) {
        return event;
      }

      try {
        // Categorize the error
        const category = categorizeError(event as Sentry.ErrorEvent, hint);

        // Sample expected 401/404 API errors at 2%. A dropped event never leaves
        // the browser, so it also cannot trigger an error replay.
        const isSampledExpectedApiError = isExpectedApiError(
          category,
          event,
          hint
        );
        if (
          isSampledExpectedApiError &&
          Math.random() >= EXPECTED_API_SAMPLE_RATE
        ) {
          return null;
        }

        const updatedEvent: Sentry.Event = {
          ...event,
          level:
            category.issue_category === 'api_error' ? 'debug' : event.level,
          tags: {
            ...event.tags,
            issue_category: category.issue_category,
            issue_type: category.issue_type,
            priority: category.priority,
            group: category.group,
            confidence: category.confidence,
            // Tells the reader of the issue count to multiply by 50.
            ...(isSampledExpectedApiError && {
              sample_rate: String(EXPECTED_API_SAMPLE_RATE)
            })
          },
          contexts: {
            ...event.contexts,
            categorization: {
              description: getCategoryDescription(category),
              automated: true,
              timestamp: new Date().toISOString(),
              ...category
            }
          },
          fingerprint: [
            category.issue_category,
            category.issue_type,
            event.exception?.values?.[0]?.type || 'unknown'
          ]
        };
        return updatedEvent;
      } catch (categorizationError) {
        // If categorization fails, add fallback tags
        const fallbackEvent = {
          ...event,
          tags: {
            ...event.tags,
            categorization_failed: 'true',
            priority: 'medium'
          }
        };

        console.warn('Error categorization failed:', categorizationError);
        console.warn('Fallback Event:', fallbackEvent);
        return fallbackEvent;
      }
    },
    ignoreErrors: [
      'NetworkError',
      'Network Error',
      'timeout exceeded',
      'Failed to load Stripe.js'
    ]
  });

  // Session Replay is ~40KB gzip and isn't needed for first paint. Loading it at
  // module-eval made it compete for bandwidth with the destination chunk on the
  // app→webview path, so hold it until the page has loaded and the main thread is
  // idle. Replay only buffers from the moment it's added, so this widens the
  // window in which a boot-time error has no replay attached.
  const loadReplay = (): void => {
    import('./sentryReplay').catch((err) =>
      console.warn('Sentry: Session Replay failed to load', err)
    );
  };

  const scheduleReplay = (): void => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(loadReplay, { timeout: 2000 });
    } else {
      // Safari only shipped requestIdleCallback in 17.4; our floor is iOS 14.
      setTimeout(loadReplay, 200);
    }
  };

  if (document.readyState === 'complete') {
    scheduleReplay();
  } else {
    window.addEventListener('load', scheduleReplay, { once: true });
  }
}
